import dayjs from 'dayjs'
import { initializeApp } from 'firebase/app'
import {
  Auth,
  connectAuthEmulator,
  getAuth,
  PhoneAuthProvider,
  signInWithCredential,
  signInWithCustomToken,
} from 'firebase/auth'
import constants from '~/common/config/config'

// When loaded in a WebView (emulator or physical device), localhost refers to the
// device itself, not the host machine. Use the hostname from the current URL so
// service requests reach the dev server on the host machine.
const getLocalHost = () => {
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return window.location.hostname
  }
  return import.meta.env.VITE_LOCAL_HOST_IP || 'localhost'
}

function setupFirebaseAuth(): Auth {
  // Initialize Firebase App and Auth
  const app = initializeApp(constants.firebaseConfig)
  const auth = getAuth(app)

  if (!constants.isStaging && !constants.isProduction) {
    connectAuthEmulator(auth, `http://${getLocalHost()}:9099`, { disableWarnings: true })
  }

  return auth
}

/**
 * Gets the user token for API authentication.
 *
 * NOTE: With sealed session cookies, authentication is handled automatically
 * by the browser sending the httpOnly cookie with every request.
 * This function now only handles Firebase auth fallback for legacy support.
 *
 * For WorkOS authentication:
 * - No token needed in Authorization header
 * - Sealed session cookie sent automatically by browser (credentials: 'include')
 * - Backend middleware extracts and validates token from cookie
 */
export const getUserToken = async () => {
  // Check if user is authenticated via Firebase (legacy)
  const currentUser = auth.currentUser
  if (!currentUser) {
    return ''
  }

  // Auto log out doss support users every 7 days so that team members who leave the company
  // are logged out of the system
  const isDossUser = currentUser.email?.endsWith('@doss.com')
  const isSupportUser = currentUser.email?.startsWith('support+')
  if (isDossUser && isSupportUser) {
    const tokenData = await currentUser.getIdTokenResult()
    const authTime = (tokenData.claims.auth_time ?? 0) as number
    const currentTime = dayjs().unix()
    const sevenDays = 7 * 24 * 60 * 60 * 1000
    if (currentTime - authTime > sevenDays) {
      await auth.signOut()
    }
  }

  return currentUser.getIdToken()
}

/**
 * Gets the bearer token for the logged in user
 */
export const getUserBearerToken = async () => {
  const token = await getUserToken()
  if (!token) {
    return ''
  }
  return `Bearer ${token}`
}

/**
 * Sends a phone verification code to the user
 */
export const sendPhoneVerificationCode = async (phoneNumber: string) => {
  const provider = new PhoneAuthProvider(auth)
  if (!window.recaptchaVerifier) {
    throw new Error('Recaptcha verifier not found')
  }
  const verificationId = await provider.verifyPhoneNumber(phoneNumber, window.recaptchaVerifier)
  return verificationId
}

/**
 * Verifies a phone code
 */
export const verifyPhoneCode = async (verificationId: string, code: string) => {
  const credential = PhoneAuthProvider.credential(verificationId, code)
  return signInWithCredential(auth, credential)
}

/**
 * Signs in with a custom firebase token
 */
export const signInWithCustomFirebaseToken = async (token: string) => {
  signInWithCustomToken(auth, token)
}

export const auth = setupFirebaseAuth()
